/**
 * Reports page
 */

import { useState, useEffect } from 'react';
import { listReports, generateReport, downloadReport } from '../services/reports';
import { listChecklistRuns } from '../services/checklists';
import type { Report, ChecklistRun } from '../../../shared/src/types';
import '../styles/Reports.css';

export default function Reports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [checklistRuns, setChecklistRuns] = useState<ChecklistRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [reps, runs] = await Promise.all([
        listReports(),
        listChecklistRuns(),
      ]);
      setReports(reps);
      setChecklistRuns(runs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      setGenerating(true);
      setError('');

      const data = {
        checklist_run_id: formData.get('checklist_run_id') as string,
      };

      await generateReport(data);
      setShowGenerateForm(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error generating report');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (reportId: string) => {
    try {
      const blob = await downloadReport(reportId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error downloading report');
    }
  };

  if (loading) {
    return (
      <div className="reports">
        <div className="page-header">
          <h1>Informes</h1>
        </div>
        <div className="loading">Cargando informes...</div>
      </div>
    );
  }

  return (
    <div className="reports">
      <div className="page-header">
        <h1>Informes</h1>
        <button
          className="btn-primary"
          onClick={() => setShowGenerateForm(!showGenerateForm)}
        >
          {showGenerateForm ? 'Cancelar' : 'Generar informe'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showGenerateForm && (
        <div className="create-form-card">
          <h2>Generar nuevo informe</h2>
          <form onSubmit={handleGenerate}>
            <div className="form-field">
              <label htmlFor="checklist_run_id">Checklist *</label>
              <select id="checklist_run_id" name="checklist_run_id" required>
                <option value="">Seleccionar checklist</option>
                {checklistRuns.map((run) => (
                  <option key={run.id} value={run.id}>
                    Checklist #{run.id.substring(0, 8)} - {run.installation_id.substring(0, 8)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={generating}>
                {generating ? 'Generando...' : 'Generar informe'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowGenerateForm(false)}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {reports.length === 0 ? (
        <div className="empty-state">
          <p>No hay informes generados</p>
          <button className="btn-primary" onClick={() => setShowGenerateForm(true)}>
            Generar informe
          </button>
        </div>
      ) : (
        <div className="reports-list">
          {reports.map((rep) => (
            <div key={rep.id} className="report-card">
              <div className="card-header">
                <h3>Informe #{rep.id.substring(0, 8)}</h3>
                <span className="report-version">v{rep.version}</span>
              </div>
              <div className="card-body">
                <div className="card-info">
                  <span className="info-label">Instalación:</span>
                  <span className="info-value">{rep.installation_id.substring(0, 8)}...</span>
                </div>
                <div className="card-info">
                  <span className="info-label">Generado:</span>
                  <span className="info-value">
                    {new Date(rep.generated_at).toLocaleString('es-ES')}
                  </span>
                </div>
                <div className="card-info">
                  <span className="info-label">Formato:</span>
                  <span className="info-value">{rep.format}</span>
                </div>
              </div>
              <div className="card-actions">
                <button
                  className="btn-primary"
                  onClick={() => handleDownload(rep.id)}
                >
                  Descargar PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
